<template>
    <form
        class="bg-white w-full shadow md:rounded-lg protected-verified protected-auth"
        @submit.prevent.stop="create"
    >
        <div class="py-6 max-md:container md:pt-5 md:px-6">
            <div class="mb-5">
                <span
                    class="inline-block text-lg font-medium h-5"
                    v-text="t('Create a post')"
                />
                <span
                    v-if="createPopupIsOpen"
                    class="icon icon-close h-5 w-5 cursor-pointer float-right text-slate-400 hover:text-slate-600 hidden md:inline-block"
                    @click="closeCreatePopup"
                />
            </div>
            <div class="inline-flex w-full relative">
                <UserAvatar
                    :name="isLoggedIn ? user.username : ''"
                    :animated-avatar="isLoggedIn ? user.gamify_avatar_url : null"
                    :picture="isLoggedIn ? userPicture : null"
                    :show-details="false"
                    :is-anonymous="post.postAnonymous && createPopupIsOpen"
                    :show-admin-badge="isLoggedIn ? user.hasAdminBadge : false"
                    class="mr-4 h-10"
                    size="sm"
                />
                <div class="w-full">
                    <textarea
                        ref="text"
                        v-model="post.text"
                        v-dynamic-height
                        :class="[
                            {
                                'md:pr-26': !createPopupIsOpen,
                                'md:error': validation.errors.text,
                            },
                            'custom-scrollbar max-h-66 outline-none resize-none min-h-[2.5rem]',
                        ]"
                        :readonly="!createPopupIsOpen"
                        :placeholder="t('askQuestion')"
                        type="text"
                        class="py-1 px-2 rounded"
                        @click="openCreatePopup()"
                    />
                    <transition name="fade">
                        <p
                            v-if="validation.errors.text"
                            class="px-3 text-red-600 text-sm hidden md:block"
                            v-text="t(validation.errors.text)"
                        />
                    </transition>
                </div>
                <div
                    v-if="!createPopupIsOpen && screen.minMD"
                    class="absolute mr-2 py-2 right-0 leading-4 items-center hidden md:flex"
                >
                    <Btn
                        v-tooltip="{
                            content: t('tooltip.anonymity.postCreateIcon', { link: anonymityTagUrl }),
                            disabled: isAnonymityEnabled,
                            theme: 'infoTooltip',
                        }"
                        data-testid="post-create-form_btn-anonymous"
                        variant="tonal"
                        :class="['mr-2', user?.is_premium ? 'text-slate-400' : 'text-purple-500']"
                        :content-color="user?.is_premium ? 'slate-600' : 'purple-500'"
                        icon="incognito"
                        size="xxs"
                        :disabled="!isAnonymityEnabled"
                        color-on-touch
                        @click.prevent.stop="openAnonymous()"
                    />
                    <Btn
                        data-testid="post-create-form_btn-image"
                        variant="tonal"
                        class="mr-2 text-slate-400"
                        content-color="slate-600"
                        icon="camera"
                        size="xxs"
                        color-on-touch
                        @click.prevent.stop="openPhoto()"
                    />
                    <Btn
                        data-testid="post-create-form_btn-poll"
                        variant="tonal"
                        class="mr-2 text-slate-400"
                        content-color="slate-600"
                        icon="poll"
                        size="xxs"
                        color-on-touch
                        @click.prevent.stop="openPoll()"
                    />
                    <Btn
                        v-if="!isLoggedIn"
                        data-testid="post-create-form_btn-send"
                        variant="tonal"
                        class="mr-2 text-slate-400"
                        content-color="slate-600"
                        icon="send"
                        size="xxs"
                        color-on-touch
                    />
                </div>
            </div>
            <div
                v-if="createPopupIsOpen"
                class="hidden md:inline-flex w-full h-10 mt-6 pl-13"
            >
                <Btn
                    v-if="isAnonymityEnabled"
                    v-tooltip="{
                        content: t('Anonym'),
                        disabled: showButtonsLabel,
                    }"
                    data-testid="post-create-form_btn-popup-anonymous"
                    variant="tonal"
                    :class="['btn-anonymous mr-4 leading-10', user?.is_premium ? 'text-slate-400' : 'text-purple-500']"
                    :content-color="user?.is_premium ? 'slate-600' : 'purple-500'"
                    active-color="blue-600"
                    active-content-color="blue-600"
                    icon="incognito"
                    size="xs"
                    :label="showButtonsLabel ? t('Anonym') : null"
                    :active="post.postAnonymous"
                    color-on-touch
                    @click.prevent.stop="toggle('postAnonymous')"
                />
                <Btn
                    v-tooltip="{
                        content: t('Photo'),
                        disabled: showButtonsLabel,
                    }"
                    data-testid="post-create-form_btn-popup-image"
                    variant="tonal"
                    class="mr-4 leading-10 text-slate-400"
                    content-color="slate-600"
                    active-color="blue-600"
                    active-content-color="blue-600"
                    icon="camera"
                    size="xs"
                    :label="showButtonsLabel ? t('Photo') : null"
                    :active="post.hasPhoto"
                    color-on-touch
                    @click.prevent.stop="toggle('hasPhoto')"
                />
                <Btn
                    v-tooltip="{
                        content: t('Survey'),
                        disabled: showButtonsLabel,
                    }"
                    data-testid="post-create-form_btn-popup-poll"
                    variant="tonal"
                    class="mr-4 leading-10 text-slate-400"
                    content-color="slate-600"
                    active-color="blue-600"
                    active-content-color="blue-600"
                    icon="poll"
                    size="xs"
                    :label="showButtonsLabel ? t('Survey') : null"
                    :active="post.hasPoll"
                    color-on-touch
                    @click.prevent.stop="toggle('hasPoll')"
                />
                <BtnCta
                    data-testid="post-create-form_btn-send"
                    type="submit"
                    icon="send"
                    class="ml-auto"
                    size="sm"
                    :disabled="isPosting || isUploading"
                />
            </div>
        </div>
        <div
            v-if="createPopupIsOpen && screen.minMD"
            class="hidden md:block"
        >
            <transition-group name="slide-open-y">
                <!-- eslint-disable vue/no-bare-strings-in-template -->
                <PostCreatePoll
                    v-if="post.hasPoll"
                    title="poll"
                    :multi-vote="pollMultiVote"
                    style="max-height: 328px"
                    @update-multi-vote="updatePollMultiVote"
                    @update-poll-options="updatePollOptions"
                />
                <div
                    v-if="post.hasPhoto"
                    style="max-height: 130px"
                >
                    <PostPhotoUpload
                        ref="photoUpload"
                        :files="photo.files"
                        class="border-t p-4 md:px-6"
                        spacer-left-width="3.25rem"
                        title="photo"
                        @upload-start="isUploading = true"
                        @upload-end="isUploading = false"
                    />
                </div>
                <!-- eslint-enable vue/no-bare-strings-in-template -->
            </transition-group>
        </div>
    </form>
</template>

<script>
import { inject } from 'vue';
import { showSubscriptionShop } from '@helpers/subscription';
import { BtnSize } from '@components/Btn';
import PostPhotoUpload from '@components/PostItem/PostPhotoUpload.vue';
import { mapGetters, mapActions } from 'vuex';
import GtmMixin, { GtmEventName } from '@/mixins/gtmMixin';
import { featureIsEnabled } from '@/modules/features';
import route from '@router/route';
import { provideNewMarkingKey } from '@/components/keys';
import { useTranslation } from 'i18next-vue';
import PostCreatePoll from './PostCreatePoll.vue';

export default {
    name: 'PostCreateForm',
    components: {
        PostPhotoUpload,
        PostCreatePoll,
    },
    extends: GtmMixin,
    props: {
        showButtonsLabel: {
            type: Boolean,
            default: false,
        },
        contextId: {
            type: Number,
            default: undefined,
        },
        contextType: {
            type: String,
            default: '',
        },
    },
    emits: ['created'],
    setup() {
        const { t } = useTranslation('post');
        const newMarking = inject(provideNewMarkingKey);
        return { newMarking, t };
    },
    data() {
        return {
            BtnSize,
            isPosting: false,
            isUploading: false,
            post: {
                text: '',
                postAnonymous: false,
                hasPoll: false,
                hasPhoto: false,
                vue: true,
            },
            photo: {
                files: [],
            },
            validation: {
                errors: {},
                hasErrors: false,
            },
            errorItem: (message, key) => {
                return {
                    message,
                    key,
                    toString() {
                        return this.message;
                    },
                };
            },
            pollOptions: [],
            pollMultiVote: false,
        };
    },
    computed: {
        ...mapGetters('auth', ['user', 'userPicture', 'isLoggedIn']),
        ...mapGetters('user', ['inGroup', 'inCourse']),
        ...mapGetters('ui', ['screen']),
        ...mapGetters('post', ['createPopupIsOpen']),
        ...mapGetters('document', ['currentDocument']),
        isAnonymityEnabled() {
            return featureIsEnabled('anonymityIdentity') && this.user?.is_premium_system_enabled;
        },
        anonymityTagUrl() {
            return import.meta.env.MIX_ANONYMITY_TAG_URL;
        },
        isDocumentPost() {
            return this.contextType === 'document';
        },
        isJoined() {
            switch (this.contextType) {
                case 'course':
                    return this.inCourse(this.contextId);
                case 'group':
                    return this.inGroup(this.contextId);
                case 'document':
                    return this.inCourse(this.currentDocument.course.id);
                default:
                    return false;
            }
        },
    },
    mounted() {
        this.$nextTick().then(() => {
            this.post[`${this.contextType}id`] = this.contextId;
        });
    },
    methods: {
        ...mapActions('post', ['openCreatePopup', 'closeCreatePopup']),
        create() {
            if (this.isPosting || this.isUploading || !this.validate()) {
                return;
            }
            this.isPosting = true;
            let post = { ...this.post };
            if (this.post.hasPoll) {
                post = {
                    ...post,
                    pollOptions: this.pollOptions.filter((option) => {
                        return option.length > 0;
                    }),
                    multiVote: this.pollMultiVote,
                };
            }
            if (this.post.hasPhoto && this.photo.files.length > 0) {
                post = {
                    ...post,
                    files: this.photo.files,
                };
            }

            if (this.newMarking) {
                post = {
                    ...post,
                    type: 'area',
                    // The marking format returned from the backend and stored in vuex is different than expected.
                    // So we need to map the structure.
                    rect: {
                        startX: this.newMarking.xstart,
                        startY: this.newMarking.ystart,
                        w: this.newMarking.width,
                        h: this.newMarking.height,
                        page: this.newMarking.page,
                        page_width: this.newMarking.page_width,
                        page_height: this.newMarking.page_height,
                    },
                    documentPage: this.newMarking.page,
                    rotation: this.newMarking.rotation,
                };
            }
            delete post.hasPhoto;
            const uri = route('post.question.store', {
                context: this.contextType,
            });
            this.$http
                .post(uri, post)
                .then((response) => {
                    this.onCreated(response.data);
                })
                .catch(this.onError)
                .finally(() => {
                    this.isPosting = false;
                });
        },
        focusInput() {
            this.$refs.text?.focus();
        },
        /**
         * @param {import('@/api/backend/resources/PostResource').PostResource} post
         */
        onCreated(post) {
            switch (post.postDetails.context) {
                case 'course': {
                    this.pushGtmEvent(GtmEventName.CREATE_POST_COURSE);
                    break;
                }
                case 'group': {
                    this.pushGtmEvent(GtmEventName.CREATE_POST_GROUP);
                    break;
                }
                case 'document': {
                    this.pushGtmEvent(GtmEventName.CREATE_POST_DOCUMENT);
                    break;
                }
                default:
                    break;
            }

            this.reset().then(this.closeCreatePopup);
            this.$emit('created', post);
        },
        onError(error) {
            const key = new Date().getTime();
            const content = error.response?.data?.message || this.t('errors.requestError');
            this.addError('text', content, key);
            this.$store.dispatch('flashMessages/showError', { content, key });
        },
        validate() {
            this.clearValidationError();
            if (this.post.text === '') {
                this.addError('text', this.t('errors.requireQuestionText'));
            }
            if (this.post.hasPoll) {
                const options = this.pollOptions.filter((option) => option.length > 0);
                if (options.length < 2) {
                    this.addError('poll', this.t('errors.minPollOptions'));
                }
            }
            Object.keys(this.validation.errors).forEach((field) => {
                const error = this.validation.errors[field];
                this.$store.dispatch('flashMessages/showError', {
                    content: error.message,
                    key: error.key,
                });
            });
            return this.validation.hasErrors === false;
        },
        clearValidationError() {
            Object.keys(this.validation.errors).forEach((field) => {
                const error = this.validation.errors[field];
                this.$store.dispatch('flashMessages/dismiss', error.key);
            });
            this.clearValidation();
        },
        openPoll() {
            this.post.hasPhoto = false;
            this.post.hasPoll = true;
            this.openCreatePopup();
        },
        openAnonymous() {
            if (this.user?.is_premium_system_enabled && !this.user?.is_premium) {
                showSubscriptionShop('anonymous_posting_button_click');
                return;
            }
            this.post.postAnonymous = true;
            this.openCreatePopup();
        },
        async openPhoto() {
            this.post.hasPoll = false;
            this.post.hasPhoto = true;
            this.openCreatePopup();

            await this.$nextTick();
            this.$refs.photoUpload.triggerFileSelection();
        },
        toggle(type) {
            if (type === 'postAnonymous' && this.user?.is_premium_system_enabled && !this.user?.is_premium) {
                showSubscriptionShop('anonymous_posting_button_click');
                return;
            }
            const val = this.post[type];
            this.post.hasPoll = false;
            this.post.hasPhoto = false;
            this.post[type] = !val;
        },
        resizeTextarea() {
            if (this.$refs.text) {
                this.$refs.text.dispatchEvent(new CustomEvent('input'));
            }
        },
        async reset() {
            this.post.text = '';
            this.post.postAnonymous = false;
            this.post.hasPoll = false;
            this.post.hasPhoto = false;
            this.pollOptions = [];
            this.pollMultiVote = false;
            this.photo.files = [];
        },
        addError(field, message, key = null) {
            const error = [];
            error[field] = this.errorItem(message, key || new Date().getTime());
            this.validation.errors = { ...this.validation.errors, ...error };
            this.validation.hasErrors = true;
        },
        clearValidation() {
            this.validation.errors = {};
            this.validation.hasErrors = false;
        },
        updatePollMultiVote(value) {
            this.pollMultiVote = value;
        },
        updatePollOptions(updatedOptions) {
            this.pollOptions = updatedOptions;
        },
    },
};
</script>

<style lang="scss" scoped>
textarea {
    @apply outline-none resize-none;
}
</style>
